/**
 * Custom quotes (the operator-priced "Get Quote" flow).
 *
 * A trip can leave the calculator and be priced by hand. Two things put a vehicle
 * card into that flow, both decided per vehicle:
 *
 *   - `vehicles.is_quote_only`       -> always quoted, whatever the trip
 *   - `vehicles.quote_distance_miles` -> quoted once a point-to-point trip passes
 *                                        that mileage
 *
 * Both are gated by one account-level switch, `booking_custom_quotes_enabled`.
 * With it off nothing quotes: a vehicle can carry either setting and still show a
 * calculated price, so the operator has a single control that takes the whole
 * feature out of the customer's path without editing the fleet. With it on, only
 * the vehicles that opted in are affected — there is no fleet-wide mileage.
 *
 * Every gate the customer can hit (the price API, the quote-request endpoint, the
 * checkout endpoint) resolves the same way through this module, so they cannot
 * disagree about whether a booking may be priced by the calculator.
 */

/** `account_settings` key backing the feature. */
export const CUSTOM_QUOTE_SETTING_KEYS = {
  ENABLED: 'booking_custom_quotes_enabled',
} as const

/**
 * Whether the account has custom quotes switched on.
 *
 * An absent row reads as off, which is what makes this safe to ship to accounts
 * that have never configured it.
 */
export function areCustomQuotesEnabled(settingsMap: Record<string, string>): boolean {
  return settingsMap[CUSTOM_QUOTE_SETTING_KEYS.ENABLED] === 'true'
}

/** The `vehicles` columns that opt a vehicle into custom quotes. */
export interface VehicleCustomQuoteSettings {
  /** Always quoted, at any distance. */
  readonly is_quote_only?: boolean | null
  /**
   * Mileage past which a point-to-point trip is quoted. Null/absent means this
   * vehicle is never quoted for distance — there is no account-wide fallback.
   */
  readonly quote_distance_miles?: number | string | null
}

/**
 * Whether this vehicle is quote-only *in practice*.
 *
 * Callers must use this rather than reading `is_quote_only` directly, since the
 * column alone doesn't say whether the feature is switched on for the account.
 */
export function isQuoteOnlyVehicle(args: {
  readonly customQuotesEnabled: boolean
  readonly vehicle?: VehicleCustomQuoteSettings | null
}): boolean {
  return args.customQuotesEnabled && Boolean(args.vehicle?.is_quote_only)
}

export interface QuoteDistanceThreshold {
  /** Trips strictly beyond this many miles require an operator quote. */
  readonly miles: number
}

/**
 * The mileage limit that applies to one vehicle, or null when distance never
 * sends its trips to a quote.
 *
 * Null covers three cases that behave identically: the account switch is off,
 * the vehicle set no mileage, or the stored mileage is unusable (non-positive or
 * unparseable). Treating a bad value as "no limit" rather than as a limit of 0
 * keeps a data glitch from quoting every trip on that vehicle.
 *
 * Quote-only vehicles don't need a mileage — they're handled by
 * `isQuoteOnlyVehicle` and never reach a distance comparison.
 */
export function resolveVehicleQuoteDistanceThreshold(args: {
  readonly customQuotesEnabled: boolean
  readonly vehicle?: VehicleCustomQuoteSettings | null
}): QuoteDistanceThreshold | null {
  const { customQuotesEnabled, vehicle } = args

  if (!customQuotesEnabled) return null

  const miles = Number(vehicle?.quote_distance_miles)
  if (!Number.isFinite(miles) || miles <= 0) return null

  return { miles }
}

export interface RequiresDistanceQuoteParams {
  /**
   * The limit that applies to this vehicle, from
   * `resolveVehicleQuoteDistanceThreshold`, or null when distance doesn't apply.
   */
  readonly threshold: QuoteDistanceThreshold | null
  readonly serviceType: string
  /** Trip distance from the distance provider; undefined when unknown. */
  readonly distanceMiles?: number
  /**
   * True when a zone/ZIP flat rate priced this trip. A configured flat rate is
   * the operator having already priced that exact route by hand, so it keeps its
   * price no matter how long the trip is.
   */
  readonly isFlatRate?: boolean
}

/**
 * True when this trip must be quoted by the operator because of its distance.
 *
 * Only point-to-point trips have a distance to measure; hourly charters are
 * priced by duration and are never caught by this rule. An unknown distance
 * (distance provider unavailable) also never triggers it — failing open keeps
 * the booking flow working rather than pushing every trip to a manual quote
 * when Google Maps is down.
 */
export function requiresDistanceQuote(params: RequiresDistanceQuoteParams): boolean {
  const { threshold, serviceType, distanceMiles, isFlatRate } = params

  if (!threshold) return false
  if (isFlatRate) return false
  if (serviceType !== 'point_to_point') return false
  if (typeof distanceMiles !== 'number' || !Number.isFinite(distanceMiles)) return false

  return distanceMiles > threshold.miles
}
